import React, { useEffect, useRef, useState } from 'react';
import { useAppSelector } from '../../redux/hooks';
import { selectAppLogo } from '../../redux/selectors';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';

import personIcon from '../../assets/icons/darkPerson.svg'
import { createImageUrl } from '../../services/data';
import { safePlay } from '../../utils/errorHandler';
import SoundWave from '../../assets/icons/soundWave.svg?react'
import styles from './Message.module.css'
import { useTheme } from '../../themes/theme';
import FollowupChips from './FollowupChips';

interface MessageProps {
  author: string;
  content: React.ReactNode;
  audio?: string;
  followups?: string[];
  onFollowupClick?: (text: string) => void;
  isLatestAgent?: boolean;
  isLoading?: boolean;
  agentAvatar?: string;
  fontSize?: string;
  hideUserIcon?: boolean;
}

const Message = ({ author, content, audio, followups, onFollowupClick, isLatestAgent, isLoading, agentAvatar, fontSize, hideUserIcon }: MessageProps) => {

    const [isPlaying, setIsPlaying] = useState<boolean>(false)
    const theme = useTheme()
    const audioRef = useRef<HTMLAudioElement>(null)
    const appLogo = useAppSelector(selectAppLogo)

    useEffect(() => {
        if (audioRef.current) {
            if (isPlaying) {
                safePlay(audioRef.current)
            } else {
                audioRef.current.pause()
            }
        }
    }, [isPlaying])

    const togle = () => {
        setIsPlaying(!isPlaying)
    }

    return (
        <div className={`${styles.message} ${styles[author]}`} >
            <audio ref={audioRef} src={audio} onEnded={() => setIsPlaying(false)}/> 
            {!(author === 'me' && hideUserIcon) && (
                <div className={styles['icon-wrapper']} style={{ backgroundColor: author === 'me' ? theme.components.message?.client.icon : theme.components.message?.agent.icon }}>
                    <img
                        src={author === 'agent' ? (agentAvatar || createImageUrl(appLogo)) : personIcon}
                        alt={`${author} icon`}
                        style={author === 'agent' && agentAvatar ? {
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            borderRadius: '50%',
                        } : undefined}
                    />
                </div>
            )}
            <div className={styles['message-body']}>
                <Box className="message-content" dir="auto" sx={{
                    padding: '1.4rem 1.5rem',
                    borderRadius: '1.1rem',
                    backgroundColor: author === 'agent' ? theme.components.message?.agent.message : theme.components.message?.client.message,
                    marginTop: '0.6rem',
                    textAlign: 'start',
                }}>
                    <Typography component='div' variant='body1'
                        sx={{
                            color: 'black',
                            fontSize: fontSize || undefined,
                        }}
                    >
                        {content}
                    </Typography>
                </Box>
                {
                    audio &&
                    <IconButton aria-label='play pause audio'
                        onClick={togle}
                        disableRipple={true}
                        sx={{
                            backgroundColor: theme.components.message?.agent.message,
                            p: '0.5rem',
                            width: '5vh',
                            height: '5vh'
                        }}
                    >
                        <SoundWave style={{ height: '100%', width: '100%'}}/>
                    </IconButton>
                }
            </div>
            {
                followups && followups.length > 0 && isLatestAgent && onFollowupClick &&
                <div style={{ paddingTop: 40 }}>
                    <FollowupChips followups={followups} onSelect={onFollowupClick} disabled={isLoading} />
                </div>
            }

        </div>
    );
};

export default Message;
